import { useEffect, useState, forwardRef, useRef } from "react";
import { Holder } from "./Holder";
import { useCountryCode } from "@/hooks/useRequest/useCountryCode";
import { useRouter } from "next/router";
import { RegionModel } from "@/interfaces/loginTypes";
import { useTranslation } from "next-i18next";
import Popup from "@/components/popup-v2";
import SafeArea from "@/components/safe-area";
import { SearchInput, SelectView } from "@/components/search-area";
import Input from "@/components/Input";
import useMobile from "@/hooks/useMobile";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export enum ClickEventType {
	holdClick,
	toggleClick,
	inputClick,
}

interface Props {
	onChange: (val: string) => void;
	value?: string;
	defaultValue?: string;
	name?: string; // react-hook-form必须传
	onClickEvent?: (type: ClickEventType) => void;
	placeholder?: string;
}

const PhoneInput = forwardRef<HTMLSelectElement, Props>((props, ref) => {
	const router = useRouter();
	const inpRef = useRef<any>(null);
	const { t } = useTranslation("common");
	const [selectCountry, setSelectCountry] = useState(() => {
		return router.locale === "zh-Hans"
			? {
					acronym: "CN",
					phoneCode: "86",
				}
			: {
					acronym: "AE",
					phoneCode: "971",
				};
	});

	const [phoneNumber, setPhoneNumber] = useState("");
	const [searchText, setSearchText] = useState("");
	const [visible, setVisible] = useState(false);
	const isMobile = useMobile();

	const { data: countryOptions } = useCountryCode(router.locale || "en");

	useEffect(() => {
		const _defaultValue = props.defaultValue || props.value;
		if (!_defaultValue) {
			return;
		}
		const _phoneCode = _defaultValue.substring(0, 4);
		const _phoneNumber = _defaultValue.substring(4);
		setPhoneNumber(_phoneCode);
		setPhoneNumber(_phoneNumber);
	}, [props.defaultValue, props.value]);

	useEffect(() => {
		const fullValue = `${selectCountry.phoneCode.padStart(4, "0")}${phoneNumber}`;
		props.onChange(fullValue);
	}, [phoneNumber, selectCountry.phoneCode]);

	const filterOptions = (filterText: string, defaultOptions: RegionModel[]) => {
		if (!filterText) return defaultOptions.map(transformCountryOptions);
		return countryOptions.filter((item) => filterWithText(filterText, item)).map(transformCountryOptions);
	};
	const filterWithText = (text: string, item: RegionModel) => {
		const _text = text ? text.trim().replaceAll("+", "") : "";

		return (
			item.phoneCode.includes(_text) ||
			item.acronym.toLocaleLowerCase().includes(_text.toLocaleLowerCase()) ||
			item.name.toLocaleLowerCase().includes(_text.toLocaleLowerCase())
		);
	};
	const transformCountryOptions = (item: RegionModel) => {
		return {
			label: `${item.name} +${item.phoneCode}`,
			acronym: item.acronym,
			value: item.phoneCode,
		};
	};

	return (
		<>
			<Input
				name="phone"
				value={phoneNumber}
				onChange={(e) => setPhoneNumber(e)}
				aria-label={props.placeholder || t("phone-number")}
				placeholder={props.placeholder || t("phone-number")}
				id="_phone"
				ref={inpRef}
				leading={
					<Holder
						acronym={selectCountry.acronym}
						phoneCode={selectCountry.phoneCode}
						onClick={() => {
							setTimeout(() => {
								inpRef.current?.blur();
								setVisible(true);
								props.onClickEvent && props.onClickEvent(ClickEventType.holdClick);
							}, 0);
						}}
					/>
				}
				onClick={() => {
					props.onClickEvent && props.onClickEvent(ClickEventType.inputClick);
				}}
			/>

			{isMobile ? (
				<Popup visible={visible} onMaskClick={() => setVisible(false)} destroyOnClose>
					<div>
						<SearchInput
							placeholder={t("search")}
							value={searchText}
							onClear={() => setSearchText("")}
							onChange={(e) => setSearchText(e)}
							onClick={() => {
								props.onClickEvent && props.onClickEvent(ClickEventType.inputClick);
							}}
						/>

						<SelectView
							list={filterOptions(searchText, countryOptions)}
							onSelected={(e) => {
								setSelectCountry({
									acronym: e.acronym || "",
									phoneCode: e.value,
								});
								setVisible(false);
								props.onClickEvent && props.onClickEvent(ClickEventType.toggleClick);
							}}
						/>
						<SafeArea position="bottom" />
					</div>
				</Popup>
			) : (
				<Popover open={visible} onOpenChange={(open) => !open && setVisible(false)}>
					<PopoverTrigger asChild>{<div />}</PopoverTrigger>

					<PopoverContent align="start" className="w-80 px-0 py-5">
						<div>
							<SearchInput
								placeholder={t("search")}
								value={searchText}
								onClear={() => setSearchText("")}
								onChange={(e) => setSearchText(e)}
								onClick={() => {
									props.onClickEvent && props.onClickEvent(ClickEventType.inputClick);
								}}
							/>

							<SelectView
								list={filterOptions(searchText, countryOptions)}
								onSelected={(e) => {
									setSelectCountry({
										acronym: e.acronym || "",
										phoneCode: e.value,
									});
									setVisible(false);
									props.onClickEvent && props.onClickEvent(ClickEventType.toggleClick);
								}}
							/>
							<SafeArea position="bottom" />
						</div>
					</PopoverContent>
				</Popover>
			)}
		</>
	);
});

PhoneInput.displayName = "PhoneInput";
export default PhoneInput;
